/** @jsx jsx */
// @ts-ignore
import { jsx, Flex, Button } from 'theme-ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  CollectionType,
  PaginationType,
  ProductItemComponentType,
  ProductItemType,
} from '~/@types/models'
import CollectionRichSnippet from '~/components/rich-snippets/collection-rich-snippet'
import { space } from '~/gatsby-plugin-theme-ui'
import { isEmpty } from 'lodash'
import { useFilterContext } from '~/context/filters'
import { FilterSelection } from '~/components/filter/filterSelection'
import { FilterBadge } from '~/components/filter/filterBadge'
import { CollectionProvider } from '~/context/collection'

import useCollectionData from '~/hooks/useCollectionData'
import { useUrlFilters } from '../product-detail-v2/Provider/hooks/useUrlFilters'
import { CollectionGrid, TGridVariant } from './components/grid'
import { ICMSBanner } from '~/componentsV2/cmsBanners/types'
import { bannerInjectArray } from '~/componentsV2/cmsBanners/utils'
import { useIdentifierModal } from '~/context/identifier-modal'
import { cmsEnv } from '~/common/cms'
import { RiArrowUpLine } from '@remixicon/react'
import GridVirtualized from './components/gridVirtualized'
import { PaginatedGrid } from './components/paginateGrid'
import { BespokeInnerMenu } from '../product-detail-v2/bespoke-detail/bespoke-inner-menu'
import { buildFakeProducts } from './utils'

export const loadData = async (pageId: string, isDraft: boolean): Promise<any> => {
  const res = await fetch(
    `${cmsEnv.endpoint}/${pageId}?populate=deep${isDraft ? '&draft=true' : ''}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `bearer ${cmsEnv.token}`,
      },
    }
  )
  return (await res.json()).data
}

interface CollectionProductsProps {
  children: (data: { products: readonly ProductItemType[] }) => React.ReactNode
  pagination: PaginationType
  initialData: readonly ProductItemType[]
  collectionHandle: string
  showFilter: boolean
  filters: any
  initialItemsCount?: number
  data: CollectionType & {
    pagination: PaginationType
  }
  gridVariant: TGridVariant
  template: 'default' | 'bespoke'
  noLink?: boolean
}

const CollectionProducts: React.FC<CollectionProductsProps> = ({
  initialData,
  collectionHandle,
  data,
  gridVariant,
  collectionProducts,
  template = 'default',
  noLink = false,
}: CollectionProductsProps) => {
  const {
    selectedFilters,
    setIsLoading,
    setCurrentFilters,
    Top,
    scrollToTop,
    isLoading,
    topRef,
  } = useFilterContext()
  // const [inStockOnlyBool, setInStockOnlyBool] = useState<boolean>(true)
  const { updateFilters, clearUrlFilters } = useUrlFilters({
    dataFilters: data.filters,
    collectionHandle,
  })

  const hiddenSort = useMemo(() => {
    return data.metafields?.sortOptions || null
  }, [data.metafields?.sortOptions])

  const [draftBanners, setDraftBanners] = useState<ICMSBanner[]>([])
  const scrollButtonRef = useRef<HTMLButtonElement>(null)

  // const draftBanners = loadData('collection-banners', true)
  useEffect(() => {
    if (typeof window === 'undefined') return

    const currentUrl = new URL(window.location.href)
    const isDraft = currentUrl.searchParams.get('draft') === 'true'

    if (isDraft) {
      loadData('collection-banners', true).then(data => {
        setDraftBanners(data.filter(o => o.attributes.handle === collectionHandle))
      })
    }
  }, [])

  useEffect(() => {
    if (scrollButtonRef.current) {
      document.addEventListener('scroll', () => {
        if (scrollButtonRef.current) {
          const rect = topRef?.current?.getBoundingClientRect()
          if (rect) {
            if (rect.top < 0) {
              scrollButtonRef.current.style.display = 'block'
            } else {
              scrollButtonRef.current.style.display = 'none'
            }
          }

          const hasChat = typeof window !== 'undefined' && window?.HubSpotConversations

          if (hasChat) {
            scrollButtonRef.current.style.bottom = '100px'
          }
        }
      })
    }

    return () => {
      document.removeEventListener('scroll', () => {
        if (scrollButtonRef.current) {
          scrollButtonRef.current.style.display = 'none'
        }
      })
    }
  }, [scrollButtonRef.current])

  const { resetFilters, isFetching, setSort, sort, isInitialized, currentData } = useCollectionData(
    {
      collectionHandle,
      gridVariant,
      initialData,
      collectionProducts,
      template: data.templateKey,
    }
  )

  const { customerSegment } = useIdentifierModal()

  const [newCurrentBanner, bannerInitial] = useMemo(() => {
    const bannerData = draftBanners?.length ? draftBanners : data.banners
    const fakeProducts = buildFakeProducts(data?.products)
    const products = currentData.length ? currentData : fakeProducts

    const list = bannerInjectArray({
      banners: bannerData,
      list: products,
      visibility: customerSegment,
    })
    return [list, bannerData?.filter(o => o.visibility === 'all' || !o.visibility).length]
  }, [currentData, data.banners, customerSegment, draftBanners])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  useEffect(() => {
    if (!data?.filters) return

    setTimeout(() => {
      if (!isEmpty(selectedFilters) && selectedFilters) {
        updateFilters(selectedFilters)

        setCurrentFilters(state => ({
          ...state,
          selectedFilters,
        }))
      } else if (isEmpty(selectedFilters) && selectedFilters) {
        clearUrlFilters()
        resetFilters()
      }
    }, 300)
  }, [selectedFilters])

  return (
    <CollectionProvider collectionHandle={collectionHandle}>
      <Flex
        className={`collection-products ${gridVariant}`}
        sx={{
          flexDirection: 'column',
          marginTop: [space.md1, null],
          ...(collectionHandle === 'new-arrivals' && {
            '.ProductLabel--New': {
              display: 'none',
            },
          }),
        }}
      >
        <Flex
          sx={{
            flexDirection: ['column', null, null, 'row'],
            width: '100%',
            gap: [0, null, space.md1],
            maxWidth: '100%',
            '.infinite-scroll-component__outerdiv': {
              flex: 1,
            },
          }}
        >
          {template === 'default' && data?.filters && data?.filters.length > 0 && (
            <FilterSelection
              categories={data.filters}
              itemsCount={currentData.length}
              resetFilters={resetFilters}
            />
          )}
          <Flex
            sx={{
              flexDirection: 'column',
              maxWidth: '100%',
              flex: 1,
              position: 'relative',
              // '&::before': {
              //   content: '""',
              //   display: showFilter && isMobile ? 'block' : 'none',
              //   position: isMobile ? 'absolute' : 'unset',
              //   top: -78,
              //   left: -25,
              //   width: 'calc(100vw + 50px)',
              //   height: 'calc(100% + 62px)',
              //   zIndex: 2,
              //   backgroundColor: 'rgba(0, 0, 0, 0.30)',
              // },
            }}
          >
            <Top />
            {template === 'default' && (
              <FilterBadge
                setSort={setSort}
                hiddenSort={hiddenSort}
                hasFilters={!!data?.filters?.length}
                itemsCount={isInitialized ? currentData.length : data.pagination.total}
                sort={sort}
                collectionHandle={collectionHandle}
              />
            )}
            {newCurrentBanner.length > 0 ? (
              <GridVirtualized
                data={data}
                gridVariant={gridVariant}
                currentData={newCurrentBanner}
                initialItemsCount={initialData?.length + bannerInitial}
                template={template}
                noLink={noLink}
              />
            ) : (
              <div
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  minHeight: ['350px', null, '500px'],
                }}
              >
                No products found
              </div>
            )}

            <CollectionRichSnippet data={initialData as ProductItemComponentType[]} />
          </Flex>
        </Flex>
        <Button
          onClick={scrollToTop}
          className="scroll-to-top"
          ref={scrollButtonRef}
          sx={{
            position: 'fixed',
            zIndex: 2,
            height: '60px',
            width: '60px',
            borderRadius: '50%',
            right: '16px',
            bottom: '16px',
            display: 'none',
            cursor: 'pointer',
            backgroundColor: '#fff',
            border: '1px solid #808080',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#dee1e6',
            },
          }}
        >
          <RiArrowUpLine color="#808080" />
        </Button>
      </Flex>
    </CollectionProvider>
  )
}

export default CollectionProducts
